"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.LegalStep = void 0;
var UserRegisteredEvent = _interopRequireWildcard(require("@ori-events/user-registered"));
var _mui = require("@ori-ui/mui");
var _analyticsData = require("@ori/analytics-data");
var _globalApiClientReact = require("@ori/global-api-client-react");
var _react = require("react");
var _Localization = require("../../Localization");
var _defaultTranslations_simplifiedRegistrationBrandPartner = require("../../constants/defaultTranslations_simplified-registration-BrandPartner");
var _contexts = require("../../contexts");
var _interfaces = require("../../interfaces");
var _models = require("../../models");
var _api = require("../../models/api");
var _utils = require("../../utils");
var _BasicSkeleton = require("../BasicSkeleton");
var _Forms = require("../Forms");
var _StepsStyledComponents = require("./StepsStyledComponents");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireWildcard(e, t) {
  if ("function" == typeof WeakMap) var r = new WeakMap(),
    n = new WeakMap();
  return (_interopRequireWildcard = function (e, t) {
    if (!t && e && e.__esModule) return e;
    var o,
      i,
      f = {
        __proto__: null,
        default: e
      };
    if (null === e || "object" != typeof e && "function" != typeof e) return f;
    if (o = t ? n : r) {
      if (o.has(e)) return o.get(e);
      o.set(e, f);
    }
    for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]);
    return f;
  })(e, t);
}
const RootContainer = (0, _mui.styled)('div')(({
  theme
}) => ({
  position: 'relative',
  width: '100%'
}));
const ContentWrapper = (0, _mui.styled)('div')(({
  theme
}) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column'
}));
const handleUserRegisteredEvent = analyticsData => {
  const {
    consultantNumber,
    customerTypeId,
    registrationChannel,
    sponsorNumber,
    visitorSegment
  } = analyticsData;

  // workaround: date object contains client's timezone althrougt API returns only date portion
  const signUpDate = analyticsData.signUpDate ?? new Date();
  const signUpDateOffset = signUpDate.getTimezoneOffset() * 60000;
  const signUpDateWithoutOffset = new Date(signUpDate.getTime() - signUpDateOffset);
  const registerEventData = {
    registeredUserId: consultantNumber.toString(),
    customerTypeId: customerTypeId.toString(),
    registrationChannel: registrationChannel ?? '',
    registrationType: 'Self',
    sponsorNumber: sponsorNumber?.toString() ?? '',
    signUpDate: signUpDateWithoutOffset.toISOString(),
    visitorSegment: visitorSegment.toString(),
    placement: _analyticsData.Placement.JoinUs
  };
  window.dispatchEvent(new CustomEvent(UserRegisteredEvent.Name, {
    detail: registerEventData
  }));
};
const LegalStep = () => {
  const {
    notificationData
  } = (0, _contexts.useNotificationData)();
  const {
    setNotificationData
  } = notificationData;
  const {
    setFinished,
    setPendingRegistration
  } = (0, _contexts.useRegistrationStatus)();
  const {
    data: registrationConfig
  } = (0, _api.useRegistrationsMetadata)();
  const [submitSuccess, setSubmitSuccess] = (0, _react.useState)(false);
  const [isParentalChecked, setIsParentalChecked] = (0, _react.useState)(false);
  const {
    registrationId,
    customerDashboardUrl
  } = (0, _contexts.useConfiguration)();
  const {
    data: legalConfig
  } = (0, _api.useLegalMetadata)();
  const isNegativeApprovals = Boolean(legalConfig?.approvals?.shared?.displayAsNegative);

  // initial data
  const {
    data: legalInitialData
  } = (0, _api.useLegalData)(isNegativeApprovals);
  const {
    errorData
  } = (0, _contexts.useFormError)();
  const {
    setErrorData,
    customErrors
  } = errorData;
  const api = (0, _globalApiClientReact.useApiClient)();
  const {
    virtualPageAnalyticsData
  } = (0, _contexts.useVirtualPageAnalyticsData)();
  const {
    setPreviousStep,
    previousStep
  } = virtualPageAnalyticsData;
  const getCorrectSubmitData = data => {
    let correctData = data;
    const emptyIDCard = {
      documentsCollection: []
    };
    if (!data.identityCard?.documentsCollection) {
      correctData = {
        ...data,
        identityCard: {
          ...data.identityCard,
          ...emptyIDCard
        }
      };
    }
    if (!data.additionalIdentityCard?.documentsCollection) {
      correctData = {
        ...correctData,
        additionalIdentityCard: emptyIDCard
      };
    }
    if (!data.parentalConsent?.email && !data.parentalConsent?.telephone) {
      correctData = {
        ...correctData,
        parentalConsent: undefined
      };
    }
    return correctData;
  };
  const getAnalyticsData = async () => {
    try {
      const response = await (0, _globalApiClientReact.getCustomerRegistrationAnalytics)(api, registrationId);
      const result = new _models.FetchResult(response.status);
      if (result.isSuccess()) {
        return response.data;
      }
    } catch (error) {
      setNotificationData({
        isOpen: true,
        message: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
          id: _models.TranslationKey.cannotGetData,
          defaultMessage: _defaultTranslations_simplifiedRegistrationBrandPartner.defaultTranslations.cannotGetData
        }),
        type: _interfaces.MessageType.error
      });
    }
    return undefined;
  };
  (0, _react.useEffect)(() => {
    (0, _utils.scrollTopFunctionHelper)();
  }, []);
  (0, _react.useEffect)(() => {
    (0, _utils.virtualPageEventDispatch)('LegalInformationStep', previousStep, registrationId);
    setPreviousStep('LegalInformationStep');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- virtual page event should be fired exacly once, no need to reevaluate
  }, []);
  const handleUpdateRegistration = async () => {
    try {
      const updateResponse = await (0, _globalApiClientReact.updateCustomerRegistration)(api, registrationId, {
        loginType: 'Email',
        registrationId,
        currentStep: isParentalChecked ? 'LegalInformationStep' : 'SubmitStep'
      });
      const result = new _models.FetchResult(updateResponse.status);
      if (result.isSuccess()) {
        if (isParentalChecked) {
          setPendingRegistration(true);
        } else {
          setSubmitSuccess(true);
          const analyticsData = await getAnalyticsData();
          if (analyticsData !== undefined) {
            handleUserRegisteredEvent(analyticsData);
          }
          if (registrationConfig?.isRedirectToBasketEnabled && customerDashboardUrl && typeof window !== 'undefined') {
            window.location.href = customerDashboardUrl;
          } else {
            setFinished(true);
          }
        }
        if (customErrors && customErrors.length > 0) {
          setErrorData([]);
        }
      }
    } catch (error) {
      const errorResponse = error;
      if (errorResponse.response?.status === _globalApiClientReact.HttpStatusCodes.status400BadRequest) {
        const {
          Errors
        } = errorResponse.response.data;
        const submitErrors = Errors?.map(er => ({
          fieldName: er.PropertyName,
          errorCode: er.Code
        }));
        if (submitErrors?.length && submitErrors.length > 0) {
          setErrorData(submitErrors);
        } else {
          setErrorData([]);
        }
      } else {
        setNotificationData({
          isOpen: true,
          message: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
            id: _models.TranslationKey.cannotUpdate,
            defaultMessage: _defaultTranslations_simplifiedRegistrationBrandPartner.defaultTranslations.cannotUpdate
          }),
          type: _interfaces.MessageType.error
        });
      }
    }
  };
  const onSubmit = async data => {
    const submitData = getCorrectSubmitData(data);
    const currentSubmitData = {
      ...submitData,
      approvals: submitData.approvals && (0, _utils.applyNegative)(submitData.approvals, isNegativeApprovals),
      parentalConsent: isParentalChecked ? {
        ...submitData.parentalConsent,
        isChecked: undefined
      } : undefined
    };
    try {
      const updateResponseLegal = await (0, _globalApiClientReact.updateCustomerRegistrationLegalDetails)(api, registrationId, currentSubmitData);
      const result = new _models.FetchResult(updateResponseLegal.status);
      if (result.isSuccess()) {
        await handleUpdateRegistration();
      }
    } catch (error) {
      const errorResponse = error;
      if (errorResponse.response?.status === _globalApiClientReact.HttpStatusCodes.status400BadRequest) {
        const {
          Errors
        } = errorResponse.response.data;
        const submitErrors = Errors?.map(er => ({
          fieldName: er.PropertyName,
          errorCode: er.Code
        }));
        if (submitErrors?.length && submitErrors.length > 0) {
          setErrorData(submitErrors);
        } else {
          setErrorData([]);
        }
      } else {
        setNotificationData({
          isOpen: true,
          message: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
            id: _models.TranslationKey.cannotUpdate,
            defaultMessage: _defaultTranslations_simplifiedRegistrationBrandPartner.defaultTranslations.cannotUpdate
          }),
          type: _interfaces.MessageType.error
        });
      }
    }
  };
  const handleParentalConsent = (0, _react.useCallback)(newState => {
    setIsParentalChecked(newState);
  }, []);
  if (!legalInitialData) {
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(_BasicSkeleton.BasicSkeleton, {});
  }
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(RootContainer, {
    children: /*#__PURE__*/(0, _jsxRuntime.jsxs)(ContentWrapper, {
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_StepsStyledComponents.StyledTitle, {
        variant: "h4",
        color: "textPrimary",
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
          id: _models.TranslationKey.ThirdStep_Header,
          defaultMessage: _defaultTranslations_simplifiedRegistrationBrandPartner.defaultTranslations.ThirdStep_Header
        })
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_mui.Typography, {
        variant: "subtitle1",
        color: "textPrimary",
        textAlign: "center",
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
          id: _models.TranslationKey.ThirdStep_HeaderText,
          defaultMessage: _defaultTranslations_simplifiedRegistrationBrandPartner.defaultTranslations.ThirdStep_HeaderText
        })
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_Forms.LegalDetailsForm, {
        config: legalConfig,
        initialData: legalInitialData,
        isSuccessSubmit: submitSuccess,
        isRedirectToBasketEnabled: registrationConfig?.isRedirectToBasketEnabled ?? false,
        onSubmitForm: onSubmit,
        isParentalConsent: handleParentalConsent
      })]
    })
  });
};
exports.LegalStep = LegalStep;